import React from 'react';
import { observer, inject } from 'mobx-react';
import { observable, makeObservable } from 'mobx';
import autobind from 'autobind';
import { v4 as uuid } from 'uuid';

import { Locale } from 'chaika-enums';

import { Input, InputProps, MaskedInput, PhoneInput } from '@components/Input';
import { Textarea } from '@components/Textarea';
import { ProfileForm } from '@profile/store/ProfileForm';
import { ehrService } from '@patient/services/ehr/ehrService';
import { AppState } from '@store/App/App';
import { EhrTranslationData } from 'chaika-frontend-types/frontend-cms';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';

interface Props extends Partial<InputProps> {
    name: string;
    mask?: string;
    textarea?: boolean;
    validIfNotEmpty?: boolean;
    translationToLang?: Locale;
}

interface StoreProps {
    profileForm: ProfileForm;
    app: AppState;
}

@observer
class Component extends React.Component<Props & StoreProps> {
    @observable
    private showValidStatus: boolean = false;

    constructor(props: Props & StoreProps) {
        super(props);
        makeObservable(this);
    }

    public render(): React.ReactNode {
        const { name, profileForm, mask, validIfNotEmpty, textarea, translationToLang, ...props } = this.props;

        let value: string | number = '';
        let isValid: boolean;

        if (translationToLang) {
            const translations = profileForm.getFieldValue<Map<Locale, EhrTranslationData>>('translations');
            // @ts-expect-error
            value = translations?.get(translationToLang)?.[name] || '';
            isValid = true;
        } else {
            value = profileForm.getFieldValue<string | number>(name !== 'age' ? name : 'ageString') || '';
            isValid = profileForm.isFieldValid(name);
        }

        const showValidStatus: boolean = validIfNotEmpty
            ? Boolean(this.showValidStatus && value && value !== '')
            : this.showValidStatus;

        const commonProps = {
            ...props,
            value,
            isValid,
            showValidStatus,
            dataTest: `profilePage_${translationToLang ? `${translationToLang}_${name}` : name}`,
            onFocus: this.handleFocus,
            onBlur: this.handleInputBlur,
        };

        if (textarea) {
            return React.createElement<any>(Textarea, {
                ...commonProps,
                value: String(value),
                onChange: this.handleTextareaChange,
            });
        }

        if (props.type === 'tel') {
            return React.createElement(PhoneInput, {
                ...commonProps,
                onChangeValue: this.handleValueChange,
                onCountryChange: this.handleCountryChange,
            });
        }

        if (mask) {
            return React.createElement(MaskedInput, { ...commonProps, mask, onChangeValue: this.handleValueChange });
        }

        return React.createElement(Input, { ...commonProps, onChange: this.handleInputChange });
    }

    @autobind
    protected async handleInputBlur(event: React.ChangeEvent<HTMLInputElement>) {
        await this.updateFieldValue(event.target.value);
    }

    @autobind
    protected async handleCountryChange(value: string) {
        await this.updateFieldValue(value);
    }

    @autobind
    protected handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.handleValueChange(event.target.value);
    }

    @autobind
    protected handleTextareaChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
        this.handleValueChange(event.target.value);
    }

    @autobind
    protected handleValueChange(value: string) {
        const { name, profileForm, translationToLang } = this.props;

        if (translationToLang) {
            profileForm.setFieldTranslations(name, value, translationToLang);
        } else {
            profileForm.setFieldValue(name, value);
        }
    }

    @autobind
    protected handleFocus() {
        this.showValidStatus = true;
    }

    private async updateFieldValue(value: string) {
        const { name, profileForm, app, translationToLang } = this.props;
        const reqId = uuid();

        app.addSaveRequest(reqId);

        if (translationToLang) {
            await ehrService.updateTranslations(toArrayFromIterable(profileForm.getTranslations()));
        } else {
            await ehrService.updateFieldValue(name, value);
        }

        app.removeSaveRequest(reqId);

        if (profileForm.isFieldValid(name)) {
            this.showValidStatus = false;
        }
    }
}

export const ProfileInputContainer = inject<Props, StoreProps>(ProfileForm.Name, AppState.Name)(Component);
